import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import $api from "@/zapi-frontend/shared/api/axios";
import type { TRisk, TRiskInput } from "@/zapi-frontend/shared/types/risks";

export const RISKS_QUERY_KEY = "project-risks";

export function useRisks(projectId: string) {
  return useQuery({
    queryKey: [RISKS_QUERY_KEY, projectId],
    queryFn: async () => {
      const response = await $api.get<{ data: TRisk[] }>(`/projects/${projectId}/risks`);
      return response.data.data;
    },
    enabled: !!projectId,
    staleTime: 60000
  });
}

export function useCreateRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TRiskInput) => {
      const response = await $api.post<{ data: TRisk }>(`/projects/${projectId}/risks`, input);
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [RISKS_QUERY_KEY, projectId] })
  });
}

export function useUpdateRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ riskId, input }: { riskId: number; input: TRiskInput }) => {
      const response = await $api.patch<{ data: TRisk }>(`/projects/${projectId}/risks/${riskId}`, input);
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [RISKS_QUERY_KEY, projectId] })
  });
}

export function useAddressRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      riskId,
      plan,
      results,
      planFiles,
      resultsFiles,
      removeFileIds,
      riskFields
    }: {
      riskId: number;
      plan: string;
      results: string;
      planFiles: File[];
      resultsFiles: File[];
      removeFileIds: number[];
      riskFields?: TRiskInput;
    }) => {
      const formData = new FormData();
      if (riskFields)
        Object.entries(riskFields).forEach(([key, value]) =>
          formData.append(key, typeof value === "string" ? value.trim() : String(value))
        );
      formData.append("plan", plan);
      formData.append("results", results);
      if (removeFileIds.length > 0) formData.append("removeFileIds", removeFileIds.join(","));
      planFiles.forEach((file) => formData.append("planFiles", file));
      resultsFiles.forEach((file) => formData.append("resultsFiles", file));
      const response = await $api.post<{ data: TRisk }>(`/projects/${projectId}/risks/${riskId}/address`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [RISKS_QUERY_KEY, projectId] })
  });
}

export async function getRiskFileUrl(projectId: string, riskId: number, fileId: number): Promise<string> {
  const response = await $api.get<{ data: { url: string } }>(`/projects/${projectId}/risks/${riskId}/files/${fileId}`);
  return response.data.data.url;
}
